/**
 * Search Facets collection holds the set of SearchFacets returned by the FacetedSearch API call.
 * @class
 */
import {getPreference} from '@netx/core-user-next/utils/getPreference';
import Netx from '@netx/core/bootstrap';
import Base from '@netx/core/lib/netx-collection';
import Model from '../models/searchFacet';
import SearchFacetsSelectedCollection from './searchFacetsSelected';

export default class SearchFacets extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {boolean} */
	get hasSelection() {
		return this.models.some((facet) => {
			return facet.selectedOptions().length > 0;
		});
	}

	/** @type {string} */
	comparator(a, b) {
		a = a.get('title').toLowerCase();
		b = b.get('title').toLowerCase();

		if (a < b) return -1;
		if (b < a) return 1;

		return 0;
	}

	/** @override */
	constructor(models, opts = {}) {
		opts.model = opts.model || Model;

		super(models, opts);

		/**
		 * filterMode can be one of 'DEFAULT', 'ADDITIVE' or 'SUBTRACTIVE'
		 * See https://confluence.netx.net/display/DEV/Faceted+Search for details
		 * @memberof Netx.Collections.SearchFacets
		 * @alias filterMode
		 * @type {string}
		 * @default
		 */
		this.filterMode =
			opts.filterMode || getPreference('search.facet.filterMode') || 'DEFAULT';

		// Store selected options
		this.selected = new SearchFacetsSelectedCollection([]);
		this.selected.facetsCollection = this;
		this.addSubCollection(this.selected);

		// Hey, listen!
		this
			// This is all handled by that collection now but it is handy to see visual toggling prior to a response from the server
			// if this causes any problems- just get rid of it as it is not a must, just aesthetically pleasing
			.listenTo(this, 'change', (facet, facetOption = {}) => {
				if (!facetOption.changed || facetOption.changed.selected == null)
					return;
				this.selected.toggle(facetOption, facet);
			})
			// When we are reset or synced, update selected
			.listenTo(this, 'sync reset', () => {
				this.selected.update(this);
			});
	}

	/**
	 * Get selected facet options
	 * @return {array} Selected facets options
	 */
	selectedFacets() {
		let selection = [];
		this.each((facet) => {
			const sOpts = facet.selectedOptions();
			if (!sOpts.length) return;

			selection = selection.concat(sOpts);
		});
		return selection;
	}

	/**
	 * Get selected facet option values
	 * @return {array} Facets expected by the facetedSearch API call
	 */
	selectedValues() {
		const selection = [];
		this.each((facet) => {
			const sOpts = facet.selectedOptions();
			if (!sOpts.length) return;

			selection.push({
				id: facet.id,
				values: _.pluck(sOpts, 'value'),
			});
		});
		return selection;
	}

	/**
	 * Turn facets off
	 * This mimics a sync so that we get all the proper classes on our views and can handle the hiding
	 * of the facets view via css
	 */
	turnOff() {
		this.trigger('request:read', this, null);
		this.reset();
		this.trigger('sync', this);
	}

	/** @type {string} */
	static get __name__() {
		return 'SearchFacets';
	}
}
